import React, { useEffect, useState } from "react";
import { Routes, Route, NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, FileText, CalendarDays, Users, Newspaper, Mail, Plug, Palette, History, LogOut, Home, BarChart3, Search, UserCog, UserCircle, Sparkles, ChevronDown, LayoutList, Link2 as LinkIcon, Clock } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { applyThemeVars } from "../../components/site/ThemeApplier";
import CrudManager from "./CrudManager";
import AdminDashboard from "./AdminDashboard";
import AdminAnalytics from "./AdminAnalytics";
import AdminSeo from "./AdminSeo";
import AdminContent from "./AdminContent";
import AdminContacts from "./AdminContacts";
import AdminIntegrations from "./AdminIntegrations";
import AdminTheme from "./AdminTheme";
import AdminChangelog from "./AdminChangelog";
import AdminUsers from "./AdminUsers";
import AdminAccount from "./AdminAccount";
import AdminPages from "./AdminPages";
import AdminEmailTemplates from "./AdminEmailTemplates";
import AdminLinkAudit from "./AdminLinkAudit";
import AdminTerms from "./AdminTerms";
import AdminMembership from "./AdminMembership";

const eventFields = [
  { name: "title_en", label: "Title (EN)", type: "text" },
  { name: "title_fr", label: "Title (FR)", type: "text" },
  { name: "type", label: "Type (talk/dinner/workshop/gala)", type: "text" },
  { name: "speaker", label: "Speaker", type: "text" },
  { name: "speaker_role_en", label: "Speaker role (EN)", type: "text" },
  { name: "speaker_role_fr", label: "Speaker role (FR)", type: "text" },
  { name: "date", label: "Date (YYYY-MM-DD)", type: "text" },
  { name: "time", label: "Time", type: "text" },
  { name: "venue_en", label: "Venue (EN)", type: "text" },
  { name: "venue_fr", label: "Venue (FR)", type: "text" },
  { name: "price", label: "Price ($)", type: "number" },
  { name: "member_price", label: "Member price ($)", type: "number" },
  { name: "vip_price", label: "VIP price ($, 0 = none)", type: "number" },
  { name: "spots", label: "Spots remaining", type: "number" },
  { name: "image", label: "Cover image", type: "focal_image" },
  { name: "excerpt_en", label: "Excerpt (EN)", type: "textarea" },
  { name: "excerpt_fr", label: "Excerpt (FR)", type: "textarea" },
  { name: "description_en", label: "Description (EN)", type: "textarea" },
  { name: "description_fr", label: "Description (FR)", type: "textarea" },
  { name: "body_html_en", label: "Full write-up (EN, HTML)", type: "html" },
  { name: "body_html_fr", label: "Full write-up (FR, HTML)", type: "html" },
  { name: "featured", label: "Featured (next event)", type: "checkbox" },
  { name: "members_only", label: "Members only", type: "checkbox" },
  { name: "agenda", label: "Agenda", type: "json", addLabel: "Add agenda row",
    item: [
      { name: "time", label: "Time", type: "text" },
      { name: "item_en", label: "Item (EN)", type: "text" },
      { name: "item_fr", label: "Item (FR)", type: "text" },
    ] },
];

const memberFields = [
  { name: "name", label: "Name", type: "text" },
  { name: "role_en", label: "Role (EN)", type: "text" },
  { name: "role_fr", label: "Role (FR)", type: "text" },
  { name: "image", label: "Photo", type: "focal_image" },
  { name: "quote_en", label: "Quote (EN)", type: "textarea" },
  { name: "quote_fr", label: "Quote (FR)", type: "textarea" },
  { name: "story_html_en", label: "Her story (EN, HTML)", type: "html" },
  { name: "story_html_fr", label: "Her story (FR, HTML)", type: "html" },
  { name: "spotlight", label: "Show on homepage spotlight", type: "checkbox" },
  { name: "order", label: "Order", type: "number" },
];

const mediaFields = [
  { name: "kind", label: "Kind (blog/podcast)", type: "text" },
  { name: "category", label: "Category", type: "text" },
  { name: "title_en", label: "Title (EN)", type: "text" },
  { name: "title_fr", label: "Title (FR)", type: "text" },
  { name: "excerpt_en", label: "Excerpt (EN)", type: "textarea" },
  { name: "excerpt_fr", label: "Excerpt (FR)", type: "textarea" },
  { name: "image", label: "Cover image", type: "focal_image" },
  { name: "body_html_en", label: "Article body (EN, HTML)", type: "html" },
  { name: "body_html_fr", label: "Article body (FR, HTML)", type: "html" },
  { name: "read_time", label: "Read/Listen time", type: "text" },
  { name: "featured", label: "Featured", type: "checkbox" },
  { name: "order", label: "Order", type: "number" },
];

const tierFields = [
  { name: "key", label: "Key (explorer/member/circle_leader)", type: "text" },
  { name: "name_en", label: "Name (EN)", type: "text" },
  { name: "name_fr", label: "Name (FR)", type: "text" },
  { name: "price", label: "Price ($/mo, 0 = free)", type: "number" },
  { name: "lookup_key", label: "Stripe lookup key", type: "text" },
  { name: "popular", label: "Mark as Most Popular", type: "checkbox" },
  { name: "order", label: "Order", type: "number" },
  { name: "cta_en", label: "CTA label (EN)", type: "text" },
  { name: "cta_fr", label: "CTA label (FR)", type: "text" },
  { name: "benefits_en", label: "Benefits (EN)", type: "json", item: "string", itemLabel: "Benefit", addLabel: "Add benefit" },
  { name: "benefits_fr", label: "Benefits (FR)", type: "json", item: "string", itemLabel: "Avantage", addLabel: "Add benefit" },
];

const NAV_GROUPS = [
  { key: "overview", label: "Overview", items: [
    { to: "/admin", end: true, label: "Dashboard", Icon: LayoutDashboard },
    { to: "/admin/analytics", label: "Analytics", Icon: BarChart3 },
  ]},
  { key: "content", label: "Content", items: [
    { to: "/admin/content", label: "Content (EN/FR)", Icon: FileText },
    { to: "/admin/sections", label: "Page Builder", Icon: LayoutList },
    { to: "/admin/events", label: "Events", Icon: CalendarDays },
    { to: "/admin/members", label: "Member Spotlights", Icon: Users },
    { to: "/admin/media", label: "Media & Blog", Icon: Newspaper },
    { to: "/admin/tiers", label: "Membership Tiers", Icon: Sparkles },
  ]},
  { key: "people", label: "People", items: [
    { to: "/admin/users", label: "Users", Icon: UserCog },
    { to: "/admin/contacts", label: "Contact Inbox", Icon: Mail },
  ]},
  { key: "settings", label: "Settings", items: [
    { to: "/admin/integrations", label: "Integrations", Icon: Plug },
    { to: "/admin/membership", label: "Membership Expiry", Icon: Clock },
    { to: "/admin/emails", label: "Email Templates", Icon: Mail },
    { to: "/admin/theme", label: "Theme", Icon: Palette },
    { to: "/admin/seo", label: "SEO & GEO", Icon: Search },
    { to: "/admin/terms", label: "Terms of Use", Icon: FileText },
    { to: "/admin/links", label: "Link Audit", Icon: LinkIcon },
  ]},
  { key: "account", label: "Account & System", items: [
    { to: "/admin/account", label: "Account", Icon: UserCircle },
    { to: "/admin/changelog", label: "Version Log", Icon: History },
  ]},
];

export default function AdminApp() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const activeGroup = NAV_GROUPS.find((g) =>
    g.items.some((it) => it.end ? location.pathname === it.to : location.pathname.startsWith(it.to))
  )?.key;
  const [openGroups, setOpenGroups] = useState(() => {
    const init = {};
    NAV_GROUPS.forEach((g) => { init[g.key] = true; });
    return init;
  });
  useEffect(() => {
    if (activeGroup) setOpenGroups((p) => ({ ...p, [activeGroup]: true }));
  }, [activeGroup]);
  const toggleGroup = (key) => setOpenGroups((p) => ({ ...p, [key]: !p[key] }));

  const ADMIN_TOKENS = {
    "--background": "45 30% 98%", "--foreground": "145 25% 15%",
    "--card": "40 25% 95%", "--card-foreground": "145 25% 15%",
    "--popover": "40 25% 96%", "--popover-foreground": "145 25% 15%",
    "--primary": "145 35% 17%", "--primary-foreground": "45 30% 98%",
    "--secondary": "22 55% 54%", "--secondary-foreground": "45 30% 98%",
    "--muted": "35 20% 90%", "--muted-foreground": "145 10% 40%",
    "--accent": "35 50% 77%", "--accent-foreground": "145 25% 15%",
    "--border": "145 20% 85%", "--input": "145 20% 85%",
    "--font-display": "'Cormorant Garamond', Georgia, serif",
    "--font-body": "'Outfit', system-ui, sans-serif",
  };

  // Lock the whole document (incl. Radix portals + toasts) to light admin tokens
  // while the console is mounted; restore the saved public theme on exit.
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(ADMIN_TOKENS).forEach(([k, v]) => root.style.setProperty(k, v));
    return () => { api.get("/theme").then((r) => applyThemeVars(r.data)).catch(() => {}); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <aside className="w-64 bg-primary text-primary-foreground flex flex-col shrink-0 sticky top-0 h-screen">
        <div className="p-6 border-b border-primary-foreground/10">
          <p className="font-display text-2xl font-semibold">GYBC Admin</p>
          <p className="text-xs text-primary-foreground/50 mt-1 truncate">{user?.email}</p>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto no-scrollbar">
          {NAV_GROUPS.map((g) => (
            <div key={g.key} className="pb-1">
              <button
                onClick={() => toggleGroup(g.key)}
                data-testid={`admin-navgroup-${g.key}`}
                className="w-full flex items-center justify-between px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-primary-foreground/45 hover:text-primary-foreground/70 transition-colors"
              >
                <span>{g.label}</span>
                <ChevronDown size={14} className={`transition-transform ${openGroups[g.key] ? "" : "-rotate-90"}`} />
              </button>
              {openGroups[g.key] && (
                <div className="space-y-0.5">
                  {g.items.map((n) => (
                    <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.split(" ")[0].toLowerCase()}`}
                      className={({ isActive }) => `flex items-center gap-3 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-secondary text-secondary-foreground" : "text-primary-foreground/70 hover:bg-primary-foreground/10"}`}>
                      <n.Icon size={17} /> {n.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-primary-foreground/10 space-y-1">
          <button onClick={() => navigate("/")} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-primary-foreground/70 hover:bg-primary-foreground/10"><Home size={18} /> View site</button>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-primary-foreground/70 hover:bg-primary-foreground/10"><LogOut size={18} /> Sign out</button>
        </div>
      </aside>

      <main className="flex-1 p-6 md:p-10 overflow-y-auto max-h-screen">
        <Routes>
          <Route index element={<AdminDashboard />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="content" element={<AdminContent />} />
          <Route path="sections" element={<AdminPages />} />
          <Route path="emails" element={<AdminEmailTemplates />} />
          <Route path="links" element={<AdminLinkAudit />} />
          <Route path="terms" element={<AdminTerms />} />
          <Route path="membership" element={<AdminMembership />} />
          <Route path="events" element={<CrudManager title="Events" endpoint="events" fields={eventFields} titleField="title_en" subField="date" />} />
          <Route path="members" element={<CrudManager title="Member Spotlights" endpoint="members" fields={memberFields} titleField="name" subField="role_en" />} />
          <Route path="media" element={<CrudManager title="Media & Blog" endpoint="media" fields={mediaFields} titleField="title_en" subField="category" />} />
          <Route path="tiers" element={<CrudManager title="Membership Tiers" endpoint="membership_tiers" fields={tierFields} titleField="name_en" subField="key" />} />
          <Route path="contacts" element={<AdminContacts />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="integrations" element={<AdminIntegrations />} />
          <Route path="theme" element={<AdminTheme />} />
          <Route path="seo" element={<AdminSeo />} />
          <Route path="changelog" element={<AdminChangelog />} />
          <Route path="account" element={<AdminAccount />} />
        </Routes>
      </main>
    </div>
  );
}
